import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import PickerModal from '../components/PickerModal';
import { ANSWER_LANGUAGES, useAnswerTranslation } from '../../utils/answerTranslation';
import '../mobile.css';

// Reached from the Questions screen's "Record Answer" button. Saves the
// answer via the API, then navigates back to Questions with the updated
// question list patched in directly - Questions.js reads its list from
// route state (not from a live-refetched source), so we return a fresh
// state bundle rather than relying on the previous entry's stale state.
const RecordAnswer = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setFullFIR } = useCaseContext();

  const {
    id,
    from,
    name,
    question,
    questionIndex,
    interrogationQuestions = [],
    statement: isStatement,
  } = location.state || {};

  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAnswerLangPicker, setShowAnswerLangPicker] = useState(false);
  const [showTranslationLangPicker, setShowTranslationLangPicker] = useState(false);

  const api = createApiClient(API_URL);
  const t = useAnswerTranslation((path, body) => api.post(path, body));

  const isDisabled = loading || t.isTranslating || !answer.trim();

  const handleSave = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const answerTranslation = await t.ensureTranslation(answer);
      const response = await api.post(`/fir/iq/answer/${id}`, {
        user: from,
        name,
        question,
        answer: answer.trim(),
        answerLanguage: t.answerLanguage,
        answerTranslationLanguage: t.translationLanguage,
        answerTranslation,
      });

      setFullFIR(response.data);

      // Pull the freshly-merged answer array for this question straight out
      // of the API's response, rather than guessing how the server merged it.
      const list = from === 'Witness' ? response.data?.witnessList : response.data?.accusedList;
      const match = list?.find((entity) => entity.name === name);
      const refreshedQuestions = match?.interrogationQuestions;

      const updatedQuestions = Array.isArray(refreshedQuestions)
        ? refreshedQuestions
        : interrogationQuestions.map((q, i) => (i === questionIndex ? { ...q, answer: [answer.trim()] } : q));

      navigate('/m/fir/witnesses/questions', {
        replace: true,
        state: { id, from, name, interrogationQuestions: updatedQuestions, statement: isStatement },
      });
    } catch (err) {
      // A failed translation already shows its own message under the translation box.
      if (err?.message !== 'translate failed') setErrorMessage('Failed to save the answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!id || !question) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No question selected.</p>;

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#094268', marginBottom: '15px' }}>Record Answer</h2>

      <p style={{ fontWeight: 600, marginBottom: '10px', textAlign: 'justify' }}>{question}</p>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Answer Language</label>
      <div
        onClick={() => setShowAnswerLangPicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span>{t.answerLanguage}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Answer</label>
      <textarea
        placeholder="Type the answer here."
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={10}
        style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontFamily: 'inherit', fontSize: '14px' }}
      />

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Translate To</label>
      <div
        onClick={() => setShowTranslationLangPicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span>{t.translationLanguage}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      {t.needsTranslation && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#094268' }}>Translated Answer ({t.translationLanguage})</label>
            <button
              type="button"
              onClick={() => t.translate(answer).catch(() => {})}
              disabled={t.isTranslating || loading || !answer.trim()}
              className="m-pill-btn"
              style={{ color: '#094268', fontWeight: 600, padding: '4px 10px', fontSize: '13px' }}
            >
              {t.isTranslating ? 'Translating...' : 'Translate'}
            </button>
          </div>
          <textarea
            placeholder="The translated answer will appear here."
            value={t.translation}
            onChange={(e) => t.setTranslation(e.target.value)}
            rows={6}
            disabled={t.isTranslating || loading}
            style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', fontFamily: 'inherit', fontSize: '14px' }}
          />
          {t.translateError && <p style={{ color: '#c9302c', marginTop: '4px' }}>{t.translateError}</p>}
        </div>
      )}

      <button onClick={handleSave} disabled={isDisabled} className="m-button-primary">
        {loading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Save'}
      </button>

      {showAnswerLangPicker && (
        <PickerModal title="Answer Language" options={ANSWER_LANGUAGES} selected={t.answerLanguage} onSelect={t.setAnswerLanguage} onClose={() => setShowAnswerLangPicker(false)} />
      )}
      {showTranslationLangPicker && (
        <PickerModal title="Translate To" options={ANSWER_LANGUAGES} selected={t.translationLanguage} onSelect={t.setTranslationLanguage} onClose={() => setShowTranslationLangPicker(false)} />
      )}

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordAnswer;
